import { useEffect, useState } from "react";

import {
  lightColourForRegion,
  type BodyColour,
  type IalaMark,
  type Region,
} from "@nav-games/iala";

/// A single time-segment of a light's rhythm: how long it shows the
/// given colour (or "dark" if `colour` is null) before advancing to
/// the next segment. The sum of all `ms` in a pattern is the full
/// period of the light.
export interface RhythmSegment {
  ms: number;
  /// `null` = dark. Otherwise paint the dot in this colour. For
  /// alternating-colour lights (Al.BuY on the emergency wreck buoy)
  /// the segments cycle through different colours; for everything
  /// else the colour is constant and only on/off changes.
  colour: BodyColour | null;
}

/// Lookup table mapping the Admiralty-style abbreviation we store in
/// `mark.light.abbreviation` to a concrete timing pattern. Periods
/// are scaled-down slightly so the animation reads as a rhythm
/// without making the user wait 15 real seconds for the next flash —
/// the relative timing (e.g. nine short flashes in West cardinal vs
/// six-plus-a-long in South) is preserved, which is what matters for
/// recognition.
///
/// Keep this in sync with the abbreviations on every IalaMark.light
/// — there's a vitest case in code.test.ts that pins each
/// abbreviation in use, so adding a new one will surface here as a
/// console warning rather than silently breaking the animation.
function patternFor(
  abbreviation: string,
  colour: BodyColour,
): readonly RhythmSegment[] {
  // Helpers — keep the table dense + readable.
  const on = (ms: number): RhythmSegment => ({ ms, colour });
  const off = (ms: number): RhythmSegment => ({ ms, colour: null });

  switch (abbreviation) {
    case "Fl.R":
    case "Fl.G":
    case "Fl.Y":
      // Plain flashing — one short flash per 3s period.
      return [on(400), off(2600)];

    case "Fl(2+1).R":
    case "Fl(2+1).G":
      // Composite group flashing 2+1 — two quick flashes, gap, one
      // flash, longer gap. Used on preferred-channel marks.
      return [
        on(300),
        off(400),
        on(300),
        off(800),
        on(300),
        off(2400),
      ];

    case "Fl(2) 5s":
      // Isolated danger — two flashes within a 5-second period.
      return [on(300), off(500), on(300), off(2400)];

    case "VQ or Q":
      // North cardinal — continuous quick. We pick VQ for the
      // animation so it reads as obviously different from grouped
      // patterns.
      return [on(150), off(350)];

    case "VQ(3) 5s or Q(3) 10s":
      // East cardinal — three quicks every period.
      return [
        on(150),
        off(350),
        on(150),
        off(350),
        on(150),
        off(2350),
      ];

    case "VQ(6)+LFl 10s or Q(6)+LFl 15s":
      // South cardinal — six quicks then a long flash. The trailing
      // long-flash distinguishes south from the others.
      return [
        on(150), off(350),
        on(150), off(350),
        on(150), off(350),
        on(150), off(350),
        on(150), off(350),
        on(150), off(350),
        on(1500), off(2000),
      ];

    case "VQ(9) 10s or Q(9) 15s":
      // West cardinal — nine quicks.
      return [
        on(150), off(350),
        on(150), off(350),
        on(150), off(350),
        on(150), off(350),
        on(150), off(350),
        on(150), off(350),
        on(150), off(350),
        on(150), off(350),
        on(150), off(2000),
      ];

    case "Iso / Oc / LFl 10s / Mo(A)":
      // Safe water — one rhythm shown is Morse-A ("dit dah"). Three
      // others are equally permitted; picking one makes the animation
      // concrete + recognisable.
      return [on(300), off(400), on(900), off(2400)];

    case "Al.BuY 3s":
      // Emergency wreck — alternating blue and yellow, brief dark
      // segments between them.
      return [
        { ms: 1000, colour: "blue" },
        { ms: 250, colour: null },
        { ms: 1000, colour: "yellow" },
        { ms: 250, colour: null },
      ];

    default:
      // Anything we haven't catalogued — render as plain on/off so
      // there's at least a heartbeat. Adding a new mark? Add a
      // branch above + a row to the LIGHT_NOTATION table in
      // Reference.tsx.
      if (typeof console !== "undefined" && console.warn) {
        console.warn(`No rhythm pattern for "${abbreviation}" — falling back to plain flash`);
      }
      return [on(400), off(2600)];
  }
}

const HEX: Record<BodyColour, string> = {
  red: "#c0382b",
  green: "#3aa055",
  yellow: "#f0c020",
  black: "#1c2530",
  white: "#f5f1e6",
  blue: "#1f6fa5",
};

interface Props {
  mark: IalaMark;
  region: Region;
  size?: number;
}

/// Small flashing dot driven by the published rhythm of `mark.light`.
/// Walks a pattern[] via setTimeout, stepping to the next segment
/// when the current one's `ms` elapses. When the dot is "dark" it
/// fades to a low-opacity grey instead of disappearing, so the user
/// can still see the dot's position when it's not lit.
export function LightRhythm({ mark, region, size = 12 }: Props) {
  const baseColour = lightColourForRegion(mark, region);
  const [step, setStep] = useState(0);
  const pattern = patternFor(mark.light.abbreviation, baseColour);

  useEffect(() => {
    if (pattern.length === 0) return;
    const seg = pattern[step % pattern.length]!;
    const t = setTimeout(
      () => setStep((s) => (s + 1) % pattern.length),
      seg.ms,
    );
    return () => clearTimeout(t);
  }, [step, pattern]);

  const seg = pattern[step % pattern.length]!;
  const lit = seg.colour !== null;
  const fill = lit ? HEX[seg.colour!] : "#aab3bf";
  const opacity = lit ? 1 : 0.25;

  return (
    <span
      aria-label={`Light rhythm — ${mark.light.abbreviation}`}
      style={{
        display: "inline-block",
        width: size,
        height: size,
        borderRadius: "50%",
        background: fill,
        opacity,
        border: "1px solid rgba(0,0,0,0.3)",
        transition: "background 80ms linear, opacity 80ms linear",
      }}
    />
  );
}
